import { useState } from 'react';
import { skillsApi } from '../services/api';
import { useAlert } from '../contexts/AlertContext';

const useSkillLevelManagement = (competencies, setCompetencies, myReviewer, triggerRefresh, defaultLevelId) => {
  const { showAlert } = useAlert();
  const [loadingSegmentMap, setLoadingSegmentMap] = useState({});
  const [loadingOverallSkill, setLoadingOverallSkill] = useState({});

  // Helper functions to get competency levels
  const getSegmentLevel = (stepIndex, segmentId) => {
    
    if (!myReviewer?.id || !myReviewer?.skillsMatrixTemplate?.skillsTemplates?.[stepIndex]) {
      return defaultLevelId;
    }
    
    const reviewerId = myReviewer.id;
    
    const skillTemplateId = myReviewer.skillsMatrixTemplate.skillsTemplates[stepIndex].id;
    
    const segmentData = competencies.reviewers?.[reviewerId]?.skills?.[skillTemplateId]?.segments?.[segmentId];
    
    const competencyLevelId = segmentData?.competencyLevelId;
    
    // Return the competency level ID if it exists, otherwise return the default level ID
    const result = competencyLevelId || defaultLevelId;
    
    return result;
  };

  const getOverallLevel = (stepIndex) => {
    const reviewerId = myReviewer.id;
    const skillTemplateId = myReviewer.skillsMatrixTemplate.skillsTemplates[stepIndex].id;
    return competencies.reviewers?.[reviewerId]?.skills?.[skillTemplateId]?.overall;
  };

  // Handle segment level selection
  // segmentDefaultLevelId is optional - used to detect "not selected" for optional skills
  const handleSegmentLevelSelect = (mapping, activeStep, segmentDefaultLevelId = null) => async (levelId) => {
    try {
      const reviewerId = myReviewer.id;
      const currentSkillTemplate = myReviewer.skillsMatrixTemplate.skillsTemplates[activeStep - 1];
      const currentSkillId = currentSkillTemplate.id;
      const isOptionalSkill = currentSkillTemplate.isRequired === false;
      // Use segment-specific default level if provided, otherwise fall back to global default
      const effectiveDefaultLevelId = segmentDefaultLevelId || defaultLevelId;
      const isNotSelected = levelId === effectiveDefaultLevelId;

      // Optimistically update UI immediately to show the new selection
      // Also clear comments if selecting "not selected" on optional skill (backend does this too)
      setCompetencies(prev => ({
        ...prev,
        reviewers: {
          ...prev.reviewers,
          [reviewerId]: {
            ...prev.reviewers[reviewerId] || {},
            skills: {
              ...(prev.reviewers[reviewerId]?.skills || {}),
              [currentSkillId]: {
                ...(prev.reviewers[reviewerId]?.skills?.[currentSkillId] || {}),
                segments: {
                  ...(prev.reviewers[reviewerId]?.skills?.[currentSkillId]?.segments || {}),
                  [mapping.segment.id]: {
                    ...(prev.reviewers[reviewerId]?.skills?.[currentSkillId]?.segments?.[mapping.segment.id] || {}),
                    competencyLevelId: levelId,
                    // Clear comments if selecting "not selected" on optional skill
                    ...(isOptionalSkill && isNotSelected ? { comments: '' } : {})
                  }
                }
              }
            }
          }
        }
      }));

      // Set loading state for this segment
      setLoadingSegmentMap(prev => ({
        ...prev,
        [`${reviewerId}-${mapping.segment.id}`]: true
      }));

      // Actually save the change
      await skillsApi.updateSkillSegmentMappingCompetency(
        reviewerId,
        mapping.id,
        levelId
      );

      // No need to update state again since we've already done it optimistically
    } catch (error) {
      console.error('Error updating competency level:', error);
      showAlert(error.message || 'Failed to update competency level', 'error', error);

      // Revert the optimistic update if the API call fails
      // For simplicity, we'll refresh the entire assignment data
      triggerRefresh();
    } finally {
      // Clear loading state
      setLoadingSegmentMap(prev => ({
        ...prev,
        [`${myReviewer.id}-${mapping.segment.id}`]: false
      }));
    }
  };

  // Handle overall level selection
  // skillTemplate is optional - used to detect if skill is optional for comment clearing
  const handleOverallLevelSelect = async (reviewer, skillId, segmentId, levelId, skillTemplate = null) => {
    try {
      const reviewerId = myReviewer.id;
      const isOptionalSkill = skillTemplate?.isRequired === false;
      const isNotSelected = levelId === defaultLevelId;

      // Optimistically update UI immediately to show the new selection
      // Also clear comments if selecting "not selected" on optional skill (backend does this too)
      setCompetencies(prev => ({
        ...prev,
        reviewers: {
          ...prev.reviewers,
          [reviewerId]: {
            ...prev.reviewers[reviewerId] || {},
            skills: {
              ...(prev.reviewers[reviewerId]?.skills || {}),
              [skillId]: {
                ...(prev.reviewers[reviewerId]?.skills?.[skillId] || {}),
                overall: levelId,
                // Clear comments if selecting "not selected" on optional skill
                ...(isOptionalSkill && isNotSelected ? { comments: '' } : {})
              }
            }
          }
        }
      }));

      // Set loading state for this skill
      setLoadingOverallSkill(prev => ({
        ...prev,
        [`${reviewerId}-${skillId}`]: true
      }));

      // Actually save the change
      await skillsApi.updateSkillMappingCompetency(
        reviewerId,
        skillId,
        levelId
      );

      // No need to update state again since we've already done it optimistically
    } catch (error) {
      console.error('Error updating overall competency level:', error);
      showAlert(error.message || 'Failed to update overall competency level', 'error', error);

      // Revert the optimistic update if the API call fails
      // For simplicity, we'll refresh the entire assignment data
      triggerRefresh();
    } finally {
      // Clear loading state
      setLoadingOverallSkill(prev => ({
        ...prev,
        [`${myReviewer.id}-${skillId}`]: false
      }));
    }
  };

  return {
    // State
    loadingSegmentMap,
    loadingOverallSkill,
    
    // Helper functions
    getSegmentLevel,
    getOverallLevel,
    
    // Actions
    handleSegmentLevelSelect,
    handleOverallLevelSelect
  };
};

export default useSkillLevelManagement;